import { useState, useEffect } from "react";

import {
  useGetNoteQuery,
  useGetNoteHistoryQuery,
  useSaveNoteStatusMutation,
} from "@src/services/slices/notesApiSlice";
import { useGetLoggedInUserQuery } from "@src/services/slices/usersSlice";
import { NoteHistory, Note } from "@src/types";
import { GridApi, IRowNode } from "ag-grid-community";
import dayjs from "dayjs";

interface NoteHookProps {
  requestId: number;
  gridApi?: GridApi;
}

export const useNoteHook = ({ requestId, gridApi }: NoteHookProps) => {
  const [completeNote, setCompleteNote] = useState<string>("");

  const { data: loggedInUser } = useGetLoggedInUserQuery(null, {
    skip: !requestId,
  });
  const { data: notes, isSuccess: isGetNotesSuccess } = useGetNoteQuery(
    requestId,
    { skip: !requestId }
  );
  const { data: noteHistory, isSuccess: isGetNotesHistorySuccess } =
    useGetNoteHistoryQuery(requestId, { skip: !requestId });

  const [updateNote] = useSaveNoteStatusMutation();

  useEffect(() => {
    if (loggedInUser === undefined || !isGetNotesHistorySuccess) return;
    if (notes?.length === 0 || !isGetNotesSuccess) return;

    const updatedRequests: number[] = [];

    for (const note of notes) {
      if (note.createdBy === loggedInUser.employeeId) continue; // Skip notes created by the logged-in employee

      const isNoteRead = noteHistory?.some(
        (noteHistoryItem: NoteHistory) =>
          noteHistoryItem.noteId === note.id &&
          noteHistoryItem.readByEmployeeId === loggedInUser.employeeId
      );

      if (isNoteRead) continue; // If the note is already read by the logged-in employee, skip updating it

      const noteStatus: NoteHistory = {
        noteId: note.id,
        requestId: requestId,
        readByEmployeeId: loggedInUser.employeeId,
      };
      updateNote(noteStatus);
      updatedRequests.push(note.roleRequestId);
    }

    if (gridApi && updatedRequests.length > 0) {
      updateTableState(updatedRequests);
    }

    // this is only needed for the old view because the notes are displayed in a textarea
    // Todo: remove this when the new view is fully implemented
    setCompleteNote(
      notes.map((note: Note) => createNoteString(note)).join("\r\n")
    );
  }, [
    loggedInUser,
    isGetNotesSuccess,
    isGetNotesHistorySuccess,
    notes,
    noteHistory,
    requestId,
    updateNote,
  ]);

  /**
   * This is only needed when a update of the table is required and the rtk cache is not enough.
   *
   * @param {number[]} updatedRequestIds - An array of request IDs that have been updated.
   */
  function updateTableState(updatedRequestIds: number[] = []): void {
    for (const requestId of updatedRequestIds) {
      const node: IRowNode = gridApi.getRowNode(requestId?.toString());

      if (node) {
        node.setData({ ...node.data, isCommentRead: true });
      }
    }
  }

  /**
   * Todo: remove this when the new view is fully implemented
   * Creates a formatted string for a note.
   *
   * @param {Note} note - The note object containing details.
   * @returns {string} - The formatted string representation of the note.
   */
  function createNoteString(note: Note): string {
    const datePart = "[" + dayjs(note.createdDate).format("DD/MM/YYYY") + "]";
    return `${datePart} ${note.employeeFullName}: ${note.text}`;
  }

  return {
    notes,
    noteHistory,
    isGetNotesSuccess,
    isGetNotesHistorySuccess,
    completeNote,
  };
};
